import { getTimeAttackRecordOverviewStatTableColumns } from "components/StatsTable/ColumnConfigs/RecordTable/TimeAttackOverview";
import { getTimeAttackRecordTrackStatTableColumns } from "components/StatsTable/ColumnConfigs/RecordTable/TimeAttackTrack";
import { StatTable } from "components/StatsTable/StatTable";
import { StatTableColumn } from "components/StatsTable/StatTableColumn";
import { Switcher, SwitchOption } from "components/Switcher/Switcher";
import {
  calculateDriverTimeAttackRecords,
  TimeAttackRecordWithDisplay,
} from "model/Records old/TimeAttackRecord";
import { useState } from "react";
import { anyStringContains } from "Utility";
import { TimeAttackSet } from "model/Match/Set/TimeAttack/TimeAttackSet";
import { MatchTeams } from "model/Match/MatchTeams";
import { getTimeAttackTrackWeightHeaders } from "model/Match/Set/TimeAttack/Track/TimeAttackTrack";
import {
  selectRosterOrgDetails,
  selectTournamentDriverName,
} from "selectors/Roster";
import { selectTrackNameFromTournamentId } from "selectors/Tournament";
import { useAppSelector } from "app/hooks";

interface Props {
  set: TimeAttackSet;
  teams: MatchTeams;
  tournamentId: string;
  groups?: {
    startPosition: number;
    endPosition: number;
    color: string;
  }[];
}

export const TimeAttackDriverTable = ({
  set,
  teams,
  tournamentId,
  groups,
}: Props) => {
  const trackNames = useAppSelector((state) =>
    set.tracks.map((t) => {
      return {
        trackId: t.trackId,
        name: !!t.trackId
          ? selectTrackNameFromTournamentId(state, t.trackId, tournamentId)
          : undefined,
      };
    })
  );

  const switchOptions: SwitchOption<string>[] = [
    {
      display: "Overview",
      selectable: true,
      value: "Overview",
    },
  ];

  set.tracks.forEach((track) => {
    if (track.rounds.length > 1) {
      switchOptions.push({
        display:
          trackNames.find((t) => t.trackId === track.trackId)?.name ?? "???",
        selectable: true,
        value: track.trackId,
      });
    }
  });

  const [selectedSwitchOption, setSelectedSwitchOption] = useState(
    switchOptions[0].value
  );

  const driverRecords = calculateDriverTimeAttackRecords(set, teams);
  var statTableColumns: StatTableColumn<TimeAttackRecordWithDisplay>[] = [];
  if (selectedSwitchOption === "Overview") {
    statTableColumns = getTimeAttackRecordOverviewStatTableColumns(
      trackNames.map((t) => t.name),
      groups
    );
  } else {
    const track = set.tracks.find((t) => t.trackId === selectedSwitchOption);
    if (!track) {
      statTableColumns = getTimeAttackRecordTrackStatTableColumns(
        [],
        -1,
        undefined
      );
    } else {
      const trackIdx = set.tracks.indexOf(track);
      const trackTotals = driverRecords.flatMap(
        (r) => r.tracks[trackIdx].score ?? 0
      );
      const bestTrackTotal =
        trackTotals.length > 0 ? Math.min(...trackTotals) : undefined;
      statTableColumns = getTimeAttackRecordTrackStatTableColumns(
        getTimeAttackTrackWeightHeaders(track, set.format.scoring) ?? [],
        trackIdx,
        bestTrackTotal
      );
    }
  }

  const driverRecordsWithDisplay: TimeAttackRecordWithDisplay[] =
    useAppSelector((state) =>
      driverRecords.map((r) => {
        const { id: orgId, name: orgName } =
          selectRosterOrgDetails(state, r.rosterId, tournamentId) || {};

        const displayName = selectTournamentDriverName(
          state,
          r.teamOrDriverId,
          tournamentId
        );

        return { record: r, orgId, displayName, orgName };
      })
    );

  return (
    <>
      {switchOptions.length > 1 && (
        <Switcher
          options={switchOptions}
          selectedValue={selectedSwitchOption}
          setSelectedValue={setSelectedSwitchOption}
        />
      )}
      <StatTable
        thinner={true}
        stats={driverRecordsWithDisplay}
        columns={statTableColumns}
        initialSortColumnIdx={0}
        keyGetter={(arg) => `${arg.orgId} - ${arg.displayName}`}
        paginationAmount={40}
        searchFunction={(arg, searchText) =>
          anyStringContains(
            searchText,
            arg.displayName,
            arg.orgName,
          )
        }
        searchPlaceholder={"Filter: Org or Driver. Comma for multiple"}
        legend={
          !!groups && selectedSwitchOption === "Overview"
            ? groups.map((g) => ({
                color: g.color,
                text: g.startPosition === 1 ? "Division 1" : "Division 2",
              }))
            : undefined
        }
      />
    </>
  );
};
